import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Panel } from 'primereact/panel';
import { Divider } from 'primereact/divider';
import { ShippingLineItem, ShippingOrder } from '../../../DeShippingTableWrapper';

interface AcceptConfirmationDialogProps {
  visible: boolean;
  onHide: () => void;
  onConfirm: () => void;
  loading?: boolean;
  // For single item accept
  selectedLineItem?: ShippingLineItem | null;
  // For bulk accept
  order?: ShippingOrder | null;
  totalItems?: number;
  // Accept type
  acceptType: 'single' | 'all';
}

export const AcceptConfirmationDialog: React.FC<AcceptConfirmationDialogProps> = ({
  visible,
  onHide,
  onConfirm,
  loading = false,
  selectedLineItem,
  order,
  totalItems,
  acceptType,
}) => {
  const isBulk = acceptType === 'all';
  const itemCount = isBulk ? totalItems : 1;

  const getHeader = () => {
    return isBulk ? 'Confirm Accept All Items' : 'Confirm Accept Item';
  };

  const getMainMessage = () => {
    if (isBulk) {
      return `Are you sure you want to accept all ${itemCount} line items in this order?`;
    }
    return 'Are you sure you want to accept this item?';
  };

  const getButtonLabel = () => {
    return isBulk ? 'Accept All' : 'Accept';
  };

  const renderWarningMessage = () => {
    if (!isBulk) return null;
    
    return (
      <Message 
        severity="warn" 
        text="This action will accept all pending line items. Please ensure you have reviewed all items before proceeding."
        className="w-full"
      />
    );
  };

  const renderItemDetails = () => {
    if (isBulk && order) {
      return (
        <Panel header={`Order: ${order.order_name}`} className="p-panel-content-sm">
          <div>
            <strong>Customer:</strong> {order.email}
          </div>
          <div>
            <strong>Total Items:</strong> {itemCount}
          </div>
        </Panel>
      );
    }
    
    if (selectedLineItem) {
      return (
        <Panel header={selectedLineItem.product_name} className="p-panel-content-sm">
          <div>
            <strong>SKU:</strong> {selectedLineItem.product_sku}
          </div>
          <div>
            <strong>Quantity:</strong> {selectedLineItem.product_quantity}
          </div>
        </Panel>
      );
    }
    
    return null;
  };

  const dialogFooter = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Cancel"
        icon="pi pi-times"
        severity="danger"
        outlined
        onClick={onHide}
        disabled={loading}
        style={{ marginRight: '1rem' }}
      />
      <Button
        label={getButtonLabel()}
        icon="pi pi-check"
        severity="success"
        onClick={onConfirm}
        disabled={loading}
        loading={loading}
      />
    </div>
  );

  return (
    <Dialog
      header={getHeader()}
      visible={visible}
      style={{ width: isBulk ? '450px' : '400px' }}
      modal
      onHide={onHide}
      footer={dialogFooter}
    >
      <div className="flex flex-column gap-3">
        <p className="m-0 text-color-secondary">
          {getMainMessage()}
        </p>
        
        
        <Divider />
        
        {renderItemDetails()}

        <Divider />
        {renderWarningMessage()}
      </div>
    </Dialog>
  );
}; 